// Servicio de Insumos (Supplies)
// Gestiona los insumos del sistema

import { getCollection, getDocument, createDocument, updateDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'

const SUPPLIES_COLLECTION = 'supplies'

/**
 * Obtener todos los insumos
 * @param {boolean} includeInactive - Si incluir insumos inactivos
 * @returns {Promise<Array>} Lista de insumos
 */
export const getAllSupplies = async (includeInactive = false) => {
  try {
    const allSupplies = await getCollection(SUPPLIES_COLLECTION, [])
    
    let filtered = allSupplies
    if (!includeInactive) {
      filtered = allSupplies.filter(supply => supply.active !== false)
    }
    
    // Ordenar por nombre
    return filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error obteniendo insumos:', error)
    throw error
  }
}

/**
 * Obtener un insumo por ID
 * @param {string} supplyId - ID del insumo
 * @returns {Promise<Object|null>} Insumo o null
 */
export const getSupply = async (supplyId) => {
  try {
    return await getDocument(SUPPLIES_COLLECTION, supplyId)
  } catch (error) {
    console.error('Error obteniendo insumo:', error)
    throw error
  }
}

/**
 * Obtener insumos por proveedor
 * @param {string} supplierId - ID del proveedor (email)
 * @returns {Promise<Array>} Lista de insumos
 */
export const getSuppliesBySupplier = async (supplierId) => {
  try {
    const allSupplies = await getAllSupplies(true)
    return allSupplies.filter(supply => {
      const supplierIds = supply.supplierIds || []
      return supplierIds.includes(supplierId)
    })
  } catch (error) {
    console.error('Error obteniendo insumos por proveedor:', error)
    throw error
  }
}

/**
 * Crear un nuevo insumo
 * @param {Object} supplyData - Datos del insumo
 * @returns {Promise<string>} ID del insumo creado
 */
export const createSupply = async (supplyData) => {
  try {
    const data = {
      name: supplyData.name.trim(),
      sku: supplyData.sku.trim(),
      code: supplyData.code?.trim() || null,
      category: supplyData.category,
      unit: supplyData.unit,
      supplierIds: supplyData.supplierIds || [],
      lastPurchasePrice: null,
      averagePurchasePrice: null,
      minPurchasePrice: null,
      maxPurchasePrice: null,
      trackStock: supplyData.trackStock || false,
      currentStock: supplyData.trackStock ? (parseFloat(supplyData.currentStock) || 0) : 0,
      minStock: supplyData.trackStock && supplyData.minStock ? parseFloat(supplyData.minStock) : null,
      description: supplyData.description?.trim() || null,
      notes: supplyData.notes?.trim() || null,
      active: supplyData.active !== false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: supplyData.createdBy || null,
      lastUpdatedBy: supplyData.createdBy || null
    }
    
    const supplyId = await createDocument(SUPPLIES_COLLECTION, data)
    return supplyId
  } catch (error) {
    console.error('Error creando insumo:', error)
    throw error
  }
}

/**
 * Actualizar un insumo
 * @param {string} supplyId - ID del insumo
 * @param {Object} supplyData - Datos actualizados
 * @returns {Promise<void>}
 */
export const updateSupply = async (supplyId, supplyData) => {
  try {
    const data = {
      name: supplyData.name?.trim() || undefined,
      sku: supplyData.sku?.trim() || undefined,
      code: supplyData.code?.trim() || null,
      category: supplyData.category || undefined,
      unit: supplyData.unit || undefined,
      supplierIds: supplyData.supplierIds || undefined,
      trackStock: supplyData.trackStock !== undefined ? supplyData.trackStock : undefined,
      currentStock: supplyData.trackStock ? (parseFloat(supplyData.currentStock) || 0) : undefined,
      minStock: supplyData.trackStock && supplyData.minStock ? parseFloat(supplyData.minStock) : null,
      description: supplyData.description?.trim() || null,
      notes: supplyData.notes?.trim() || null,
      active: supplyData.active !== undefined ? supplyData.active : undefined,
      lastUpdatedBy: supplyData.lastUpdatedBy || undefined
    }
    
    // Eliminar campos undefined
    Object.keys(data).forEach(key => {
      if (data[key] === undefined) {
        delete data[key]
      }
    })
    
    await updateDocument(SUPPLIES_COLLECTION, supplyId, data)
  } catch (error) {
    console.error('Error actualizando insumo:', error)
    throw error
  }
}

/**
 * Actualizar precio de compra de un insumo
 * @param {string} supplyId - ID del insumo
 * @param {number} purchasePrice - Nuevo precio de compra
 * @returns {Promise<void>}
 */
export const updateSupplyPurchasePrice = async (supplyId, purchasePrice) => {
  try {
    const supply = await getSupply(supplyId)
    if (!supply) throw new Error('Insumo no encontrado')
    
    const updates = {
      lastPurchasePrice: purchasePrice
    }
    
    // Calcular promedio, mínimo y máximo
    const prices = [purchasePrice]
    if (supply.lastPurchasePrice) prices.push(supply.lastPurchasePrice)
    if (supply.averagePurchasePrice) prices.push(supply.averagePurchasePrice)
    
    updates.averagePurchasePrice = prices.reduce((sum, p) => sum + p, 0) / prices.length
    updates.minPurchasePrice = Math.min(...prices)
    updates.maxPurchasePrice = Math.max(...prices)
    
    await updateDocument(SUPPLIES_COLLECTION, supplyId, updates)
  } catch (error) {
    console.error('Error actualizando precio de compra del insumo:', error)
    throw error
  }
}

export default {
  getAllSupplies,
  getSupply,
  getSuppliesBySupplier,
  createSupply,
  updateSupply,
  updateSupplyPurchasePrice
}

